import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

export const useAdminAgencies = (t, i18n) => {
    // --- State ---
    const [agencies, setAgencies] = useState([]);
    const [cities, setCities] = useState([]);
    const [newAgency, setNewAgency] = useState({
        name_en: '', name_ar: '', client_email: '', client_password: '', logo: null,
        manager_name: '', phone: '', city: '', address: '', description: '', website: '', facebook: ''
    });
    const [editingAgency, setEditingAgency] = useState({});
    const [cityFilter, setCityFilter] = useState('');
    const [nameSearch, setNameSearch] = useState('');
    const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });

    const [isEditAgencyModalOpen, setIsEditAgencyModalOpen] = useState(false);
    const [isDeleteAgencyModalOpen, setIsDeleteAgencyModalOpen] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [agencyToDelete, setAgencyToDelete] = useState(null);
    const [agencyToReset, setAgencyToReset] = useState(null);

    // --- Fetch Functions ---
    const fetchAgencies = useCallback(async () => {
        try {
            const params = {};
            if (cityFilter) params.city = cityFilter;
            if (nameSearch) params.name_search = nameSearch;
            const res = await api.get('/agencies', { params });
            setAgencies(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to fetch agencies:', err);
            setAgencies([]);
        }
    }, [cityFilter, nameSearch]);

    const fetchCities = async () => {
        try {
            const res = await api.get('/agencies/cities');
            setCities(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to fetch cities:', err);
            setCities([]);
        }
    };

    useEffect(() => {
        fetchAgencies();
        fetchCities();
    }, []); // Initial fetch

    useEffect(() => {
        fetchAgencies();
    }, [fetchAgencies]);

    // --- Handlers ---
    const handleCreateAgency = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('name_en', newAgency.name_en);
            formData.append('name_ar', newAgency.name_ar);
            formData.append('client_email', newAgency.client_email);
            formData.append('client_password', newAgency.client_password);
            if (newAgency.logo) formData.append('logo', newAgency.logo);
            if (newAgency.manager_name) formData.append('manager_name', newAgency.manager_name);
            if (newAgency.phone) formData.append('phone', newAgency.phone);
            if (newAgency.city) formData.append('city', newAgency.city);
            if (newAgency.address) formData.append('address', newAgency.address);
            if (newAgency.description) formData.append('description', newAgency.description);
            if (newAgency.website) formData.append('website', newAgency.website);
            if (newAgency.facebook) formData.append('facebook', newAgency.facebook);

            await api.post('/agencies', formData);
            setNewAgency({
                name_en: '', name_ar: '', client_email: '', client_password: '', logo: null,
                manager_name: '', phone: '', city: '', address: '', description: '', website: '', facebook: ''
            });
            fetchAgencies();
            setToast({
                isVisible: true,
                message: t('agencyCreatedSuccessfully'),
                type: 'success'
            });
        } catch (err) {
            const errorMessage = err.response?.data?.detail || t('agencyCreationFailed');
            setToast({
                isVisible: true,
                message: errorMessage,
                type: 'error'
            });
        }
    };

    const handleDeleteAgency = (id) => {
        setAgencyToDelete(id);
        setIsDeleteAgencyModalOpen(true);
    };

    const confirmDeleteAgency = async () => {
        if (!agencyToDelete) return;

        try {
            await api.delete(`/agencies/${agencyToDelete}`);
            setIsDeleteAgencyModalOpen(false);
            setAgencyToDelete(null);
            fetchAgencies();

            setToast({
                isVisible: true,
                message: t('agencyDeletedSuccessfully'),
                type: 'success'
            });
        } catch (err) {
            setIsDeleteAgencyModalOpen(false);
            setAgencyToDelete(null);

            setToast({
                isVisible: true,
                message: t('agencyDeleteFailed'),
                type: 'error'
            });
        }
    };

    const handleResetPassword = (id) => {
        setAgencyToReset(id);
        setIsResetPasswordModalOpen(true);
    };

    const confirmResetPassword = async () => {
        if (!agencyToReset) return;

        try {
            const response = await api.put(`/agencies/${agencyToReset}/reset-password`);
            setIsResetPasswordModalOpen(false);
            setAgencyToReset(null);

            setToast({
                isVisible: true,
                message: `${t('passwordResetSuccess')}: ${response.data.new_password}`,
                type: 'success'
            });
        } catch (err) {
            setIsResetPasswordModalOpen(false);
            setAgencyToReset(null);

            setToast({
                isVisible: true,
                message: t('passwordResetFailed'),
                type: 'error'
            });
        }
    };

    const handleEditAgencyClick = (agency) => {
        setEditingAgency(agency);
        setIsEditAgencyModalOpen(true);
    };

    const handleEditAgencySubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('name_en', editingAgency.name_en);
            formData.append('name_ar', editingAgency.name_ar);
            if (editingAgency.manager_name) formData.append('manager_name', editingAgency.manager_name);
            if (editingAgency.phone) formData.append('phone', editingAgency.phone);
            if (editingAgency.city) formData.append('city', editingAgency.city);
            if (editingAgency.address) formData.append('address', editingAgency.address);
            if (editingAgency.description) formData.append('description', editingAgency.description);
            if (editingAgency.website) formData.append('website', editingAgency.website);
            if (editingAgency.facebook) formData.append('facebook', editingAgency.facebook);
            if (editingAgency.logo instanceof File) {
                formData.append('logo', editingAgency.logo);
            }

            await api.put(`/agencies/${editingAgency.id}`, formData);
            setIsEditAgencyModalOpen(false);
            fetchAgencies();
            setToast({
                isVisible: true,
                message: t('agencyUpdatedSuccessfully'),
                type: 'success'
            });
        } catch (err) {
            console.error(err);
            setToast({
                isVisible: true,
                message: t('errorUpdatingAgency'),
                type: 'error'
            });
        }
    };

    // Filter agencies
    const filteredAgencies = agencies.filter(agency => {
        const matchesCity = !cityFilter || agency.city === cityFilter;
        const agencyName = i18n.language === 'ar' ? (agency.name_ar || agency.name) : (agency.name_en || agency.name);
        const matchesName = !nameSearch || agencyName.toLowerCase().includes(nameSearch.toLowerCase());
        return matchesCity && matchesName;
    });

    return {
        agencies,
        cities,
        newAgency,
        setNewAgency,
        editingAgency,
        setEditingAgency,
        cityFilter,
        setCityFilter,
        nameSearch,
        setNameSearch,
        toast,
        setToast,
        isEditAgencyModalOpen,
        setIsEditAgencyModalOpen,
        isDeleteAgencyModalOpen,
        setIsDeleteAgencyModalOpen,
        isResetPasswordModalOpen,
        setIsResetPasswordModalOpen,
        agencyToDelete,
        setAgencyToDelete,
        agencyToReset,
        setAgencyToReset,
        handleCreateAgency,
        handleDeleteAgency,
        confirmDeleteAgency,
        handleResetPassword,
        confirmResetPassword,
        handleEditAgencyClick,
        handleEditAgencySubmit,
        filteredAgencies
    };
};
